import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './stilark';
import { somDemo } from './api/somDemo';
import { lavSupabaseApi, klientFraAdresse } from './api/supabase';
import type { Api } from './api/api';
import { ApiKontekst, anvendTema, gaaTil, hentTema, saetStartRute } from './lib';
import { startIndlejret } from './indlejret';
import { startPwa } from './push';
import { paaKonsol } from './pixieVaert';
import { linkRute } from './linkRute';
import { forudHent, forudHentAlle } from './sideFiler';
import { App } from './App';
import { FejlGraense, startFejlspor } from './fejlspor';
import { StagingBaand } from './Staging';

// Altid https (http-adresser fra gamle links/bogmærker): login og billeder virker kun sikkert dér
if (location.protocol === 'http:' && !/^(localhost|127\.|\[::1\])/.test(location.hostname)) location.replace('https://' + location.host + location.pathname + location.search + location.hash);
anvendTema(hentTema());
// Siderne ligger i hver sin fil. Er der kommet en ny udgivelse, mens siden var åben, findes de gamle filer ikke længere:
// hent siden igen (højst én gang i minuttet, så en rigtig netværksfejl ikke giver en løkke – den vises så af fejlgrænsen).
addEventListener('vite:preloadError', (e) => {
  try {
    const sidst = Number(sessionStorage.getItem('freemi-genindlaest') ?? 0);
    if (Date.now() - sidst < 60_000) return;
    sessionStorage.setItem('freemi-genindlaest', String(Date.now()));
  } catch { return; }
  e.preventDefault();
  location.reload();
});
// Testlinks: #admin, #kunde, #medarbejder, #saelger, #kundeservice (og #login). Ellers en intern sti (#/...) eller startsiden.
const START: Record<string, { email: string | null; rute: string }> = {
  admin: { email: 'ejer@steamgreen.dk', rute: '/admin/overblik' },
  kunde: { email: 'soeren@eksempel.dk', rute: '/mine' },
  medarbejder: { email: 'jamil@steamgreen.dk', rute: '/steamer/dag' },
  saelger: { email: 'saelger@steamgreen.dk', rute: '/admin/crm' },
  kundeservice: { email: 'kundeservice@steamgreen.dk', rute: '/admin/overblik' },
  steamer: { email: 'jamil@steamgreen.dk', rute: '/steamer/dag' },
  book: { email: null, rute: '/book' },
  kundecenter: { email: null, rute: '/kundecenter' },
  login: { email: null, rute: '/login' },
};
// Åbnet fra hjemmeskærmen (manifestets start_url ?app=1): testversionen viser valget af rolle, drift viser login
const fraApp = new URLSearchParams(location.search).get('app') === '1';
let hash = '';
try { hash = decodeURIComponent(location.hash.slice(1)); } catch { /* ingen hash */ }
// Åbnet fra klientens hjemmeside (widget.js: på siden eller i et vindue)
startIndlejret(hash);
// I drift: Freemi som app på hjemmeskærmen med push-notifikationer
startPwa((sti) => gaaTil(sti));
// Siden, adressen peger på, hentes med det samme – samtidig med resten (og testversionens data)
if (import.meta.env.MODE !== 'enkeltfil') forudHent(hash.startsWith('/') ? hash : START[hash]?.rute ?? '');

// Drift (VITE_SUPABASE_URL sat ved build): den rigtige database. Ellers testversionen med eksempeldata i browseren –
// den hentes kun dér, så drift-bundtet ikke indeholder testversionens kode og eksempeldata.
const SB_URL = import.meta.env.VITE_SUPABASE_URL;
const SB_NOEGLE = import.meta.env.VITE_SUPABASE_KEY;
const drift = !!(SB_URL && SB_NOEGLE);
const api: Api = drift ? lavSupabaseApi(SB_URL!, SB_NOEGLE!) : new (await import('./api/demo')).DemoApi();
const demo = somDemo(api);
startFejlspor(api);

const s = demo ? START[hash] : undefined;
// Link fra en login-mail (?token_hash=…&type=…) på enhver sti: /bekraeft, forsiden, /<klient>/ eller konsollen
const linkSide = drift ? linkRute(location.search, hash, paaKonsol()) : null;
if (demo && s) {
  if (s.email) demo.demoLogin(s.email); else if (hash === 'login') void demo.logUd();
  saetStartRute(s.rute);
} else if (linkSide) saetStartRute(linkSide);
else if (/^(ok|fejl)$/.test(new URLSearchParams(location.search).get('betaling') ?? '')) {
  // Tilbage fra Viva eller MobilePay (testversionen: MobilePay-linket bærer bookingens nøgle, ?mobilepay=<nøgle>)
  const q = new URLSearchParams(location.search), mp = q.get('mobilepay') ?? '';
  saetStartRute('/betaling/' + q.get('betaling') + (/^[A-Za-z0-9]{1,40}$/.test(mp) ? '/' + mp : ''));
} else if (hash.startsWith('overtag=')) saetStartRute('/admin/overblik');   // support fra konsollen (api'et logger ind)
else if (drift && paaKonsol()) saetStartRute(hash.startsWith('/pixie/konsol') ? hash : '/pixie/konsol');
else if (hash.startsWith('/')) saetStartRute(hash);
else if (fraApp) saetStartRute(drift ? '/login' : '/start');
else if (drift) saetStartRute(klientFraAdresse(location.hostname, location.pathname) ? '/login' : '/pixie');
else saetStartRute('/start');

// Testversionen: planlagte beskeder sendes, mens siden er åben (i drift gør serveren det hvert minut).
if (demo) setInterval(() => demo.tik(), 60000);

createRoot(document.getElementById('rod')!).render(
  <StrictMode>
    <ApiKontekst.Provider value={api}><StagingBaand /><FejlGraense><App /></FejlGraense></ApiKontekst.Provider>
  </StrictMode>,
);
// Testversionen (én fil): alle sider ligger allerede i filen – gør dem klar (først nu, hvor hele filen er kørt), så ingen side viser "Henter …"
if (import.meta.env.MODE === 'enkeltfil') forudHentAlle();
